/**
 * Stocktake (beta feature `stock`, OD-P4).
 *
 *   GET  /api/stocktake                          the count sheet: every live
 *                                                ingredient by storage area, with
 *                                                its pack, price and on hand
 *   POST /api/stocktake/finish                   save the counted items at once
 *   GET  /api/deliveries/received-since-count    packs received since each
 *                                                item's last count (B8)
 *
 * On hand is a running figure (receiving adds to it), so a row's
 * `on_hand_packs` is the Expected figure the next count is checked against:
 * last count plus what came in since. The received-since query only explains
 * that figure ("last count 1 + 2 received"); nothing is added twice.
 *
 * Finish (B3's contract): the app names each count sheet with a draft_id and
 * keeps it with the local draft until a 200; after any failure it sends the
 * whole current draft again with the same id, and the server treats it as the
 * same stocktake (nothing counted twice). A replay answers replayed=true and
 * writes nothing. The mutation never retries by itself: the chef's Finish is
 * the retry, so the answer is never lost silently.
 *
 * Live updates: Finish publishes `ingredient.stock_updated` and
 * `stocktake.finished` to every other tab. This tab's own events are skipped
 * by the existing `ingredient.` handlers (isMine), so the mutation refreshes
 * the ingredients, the dashboard, the sheet and the order list itself.
 */
import { queryOptions, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api, unwrap } from '@/api/client';
import type { components } from '@/api/schema';
import { registerLive } from '@/lib/live';
import { orderListKeys } from './order-list';

export type Stocktake = components['schemas']['StocktakeOut'];
export type StockArea = components['schemas']['StockAreaOut'];
export type StockRow = components['schemas']['StockRowOut'];
export type StocktakeFinishIn = components['schemas']['StocktakeFinishIn'];
export type StocktakeFinishOut = components['schemas']['StocktakeFinishOut'];
export type StockCountResult = components['schemas']['StockCountResultOut'];
export type SkippedCount = components['schemas']['SkippedCountOut'];
export type ReceivedSince = components['schemas']['ReceivedSinceOut'];

/** Received since the last count, by ingredient id. */
export type ReceivedSinceById = Record<number, ReceivedSince>;

export const stocktakeKeys = {
  all: ['stocktake'] as const,
  sheet: () => ['stocktake', 'sheet'] as const,
  receivedSince: () => ['stocktake', 'received-since'] as const,
};

export const stocktakeQuery = queryOptions({
  queryKey: stocktakeKeys.sheet(),
  queryFn: () => unwrap(api.GET('/api/stocktake')),
});

export function useStocktake(enabled = true) {
  return useQuery({ ...stocktakeQuery, enabled });
}

/** What came in since each item's last count. Only an explanation of the
 *  Expected figure, so a failure here never blocks the count: the sheet just
 *  shows no "received" note. */
export function useReceivedSince(enabled = true) {
  return useQuery({
    queryKey: stocktakeKeys.receivedSince(),
    queryFn: () => unwrap(api.GET('/api/deliveries/received-since-count')),
    select: (data): ReceivedSinceById => {
      const out: ReceivedSinceById = {};
      for (const item of data.items) out[item.ingredient_id] = item;
      return out;
    },
    retry: false,
    enabled,
  });
}

/** Everything a stock change moves: the count sheet, the order list, the
 *  ingredients list (its on-hand column) and the dashboard's stock value. */
export function refreshAfterStockChange(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: stocktakeKeys.all });
  void qc.invalidateQueries({ queryKey: orderListKeys.all });
  void qc.invalidateQueries({ queryKey: ['ingredients'] });
  void qc.invalidateQueries({ queryKey: ['dashboard'] });
}

export function useFinishStocktake() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: StocktakeFinishIn) => unwrap(api.POST('/api/stocktake/finish', { body })),
    retry: false,
    onSuccess: () => refreshAfterStockChange(qc),
  });
}

// Other tabs and other people. Any ingredient change can move the sheet (a
// new item, a rename, a price, a storage area, a delete), and
// `ingredient.stock_updated` (a stocktake, a delivery received or voided)
// starts with `ingredient.` too. Group renames change the sub-headings. The
// `stocktake.` and `delivery.` handlers are belt and braces for events that
// carry no ingredient change.
const refreshSheet = (_evt: unknown, qc: QueryClient) => void qc.invalidateQueries({ queryKey: stocktakeKeys.all });
registerLive('ingredient.', refreshSheet);
registerLive('ingredient_group.', refreshSheet);
registerLive('stocktake.', refreshSheet);
registerLive('delivery.', refreshSheet);
